import React, { useRef, useState, useEffect } from "react";
import { injectIntl } from "react-intl";
import Map from "./Map";
import { Layers, TileLayer, VectorLayer } from "./Layers";
import { Fill, Stroke, Style, Text } from "ol/style";
import { osm, vector, tileArcGISRest, wmts, wms } from "./Source";
import GeoJSON from "ol/format/GeoJSON";
import { Controls, FullScreenControl } from "./Controls";
import Interaction from "./Interaction";
import LayerSelector from "./LayerSelector";
import isEmpty from "lodash/isEmpty";
import SearchField from "./SearchField";
import proj4 from "proj4";
import { register } from "ol/proj/proj4";
import DotLoader from "../common3/Loaders/DotLoader";
import MapContext from "./Map/MapContext";
import useArcGISLayers from "./hooks/useArcGISLayers";
import {
  LockWrapper,
  LockHeader,
  MapContainer,
  LayerSelectorContainer,
  SaveButton,
  SaveInfoWrapper,
} from "./styled";
import ValidationBar from "../common3/ValidationBar";
import useWMTSLayers from "./hooks/useWMTSLayers";
import useWMSLayers from "./hooks/useWMSLayers";
import useDidMount from "../common3/hooks/useDidMount";
import createImageFromMap from "./helpers/createImageFromMap";
import transformLonLat from "./helpers/transformLonLat";
import getFeatureStyle from "./helpers/getFeatureStyle";

const styles = {
  Point: new Style({
    text: new Text({
      text: "\uf3c5",
      font: '900 32px "Font Awesome 5 Pro"',
      fill: new Fill({
        color: "red",
      }),
    }),
  }),
  Polygon: new Style({
    stroke: new Stroke({
      color: "purple",
      lineDash: [4],
      width: 3,
    }),
    fill: new Fill({
      color: "rgba(231, 111, 252, 0.1)",
    }),
  }),
  MultiPolygon: new Style({
    stroke: new Stroke({
      color: "purple",
      width: 1,
    }),
    fill: new Fill({
      color: "rgba(231, 111, 252, 0.1)",
    }),
  }),
};

const createFeatures = (features) => {
  return features ? new GeoJSON().readFeatures(features) : null;
};

const transformLocationFeature = (locationFeature) => {
  const { long = 0, lat = 0 } = locationFeature;

  return {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [Number(long), Number(lat)],
        },
        properties: {
          ...locationFeature,
          category: "Adress",
        },
      },
    ],
  };
};

const OpenLayersMAP = (props) => {
  const {
    intl,
    save,
    id,
    value = {},
    extra = {},
    disabled,
    builder = false,
    effects,
    setStop,
    setIsDndDisabled = () => {},
  } = props;
  const { features = null, locationFeatures = [] } = value;
  const {
    defaultMap = {},
    startX,
    startY,
    useArcGIS = false,
    useWMTS = false,
    useWMS = false,
    defaultBackgroundDisabled = false,
    defaultLayers = [],
    defaultBgLayers = [],
    addressSearchEnabled = false,
    backgroundUrl = "",
    foregroundUrl = "",
    projection = "EPSG:3857",
    disablePoint = false,
    disableBox = false,
    disableLine = false,
    disablePen = false,
    disableEraser = false,
  } = extra;
  const DEFAULT_COORDS =
    startX && startY
      ? [startX, startY]
      : defaultMap.center || ["13.000478", "55.603378"];
  const DEFAULT_ZOOM = 13;
  const ref = useRef();
  const hasTriggeredSearch = useRef(false);
  const [map, setMap] = useState(null);
  const [center] = useState(() => value.center || DEFAULT_COORDS);
  const [zoom] = useState(() => value.zoom || defaultMap.zoom || DEFAULT_ZOOM);
  const [drawLayer] = useState(() =>
    vector({
      features: createFeatures(features || defaultMap.features),
    })
  );
  const [_locationFeatures, setLocationFeatures] = useState(locationFeatures);
  const [selectedLayers, setSelectedLayers] = useState(defaultLayers);
  const [selectedBgLayers, setSelectedBgLayers] = useState(defaultBgLayers);
  const [isSaving, setIsSaving] = useState(false);
  const [isLocked, setIsLocked] = useState(true);
  const [drawMode, setDrawMode] = useState(false);
  const arcGISLayers = useArcGISLayers(foregroundUrl);
  const arcGISBgLayers = useArcGISLayers(backgroundUrl);
  const [wmtsLayers, wmtsXML] = useWMTSLayers(foregroundUrl);
  const [wmtsBgLayers, wmtsBgXML] = useWMTSLayers(backgroundUrl);
  const wmsLayers = useWMSLayers(foregroundUrl);
  const wmsBgLayers = useWMSLayers(backgroundUrl);
  const didMount = useDidMount();

  useEffect(() => {
    return () => {
      props.unloadEffect({ effectId: `${id}_onSearch` });
      props.unloadEffect({ effectId: `${id}_onSelect` });
    };
  }, []);

  const layers = useArcGIS
    ? arcGISLayers
    : useWMTS
    ? wmtsLayers
    : useWMS
    ? wmsLayers
    : [];
  const backgroundLayers = useArcGIS
    ? arcGISBgLayers
    : useWMTS
    ? wmtsBgLayers
    : useWMS
    ? wmsBgLayers
    : [];

  const searchResult = hasTriggeredSearch.current
    ? effects[`${id}_onSearch`]
    : [];

  const shouldDisplayToolComponents = !isLocked && !isSaving;

  const shouldDisplayToolbar =
    shouldDisplayToolComponents &&
    (!disablePoint ||
      !disableBox ||
      !disableLine ||
      !disablePen ||
      !disableEraser);

  proj4.defs([
    [
      "EPSG:3008",
      "+proj=tmerc +lat_0=0 +lon_0=13.5 +k=1 +x_0=150000 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs",
    ],
    [
      "EPSG:3010",
      "+proj=tmerc +lat_0=0 +lon_0=16.5 +k=1 +x_0=150000 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs",
    ],
  ]);

  register(proj4);

  useEffect(() => {
    if (didMount) {
      saveDefaultLayers("defaultLayers", []);
      saveDefaultLayers("defaultBgLayers", []);
    }
  }, [foregroundUrl, backgroundUrl]);

  const _save = async (
    map = { getView: () => ({ getZoom() {}, getCenter() {} }) }
  ) => {
    const features = drawLayer.getFeatures();
    let GEOJSON_PARSER = new GeoJSON();
    let featuresJSON = GEOJSON_PARSER.writeFeaturesObject(features);

    const zoom = map.getView().getZoom();
    let center = map.getView().getCenter();

    const data = {
      value: {
        features: featuresJSON,
        locationFeatures: _locationFeatures,
        zoom,
        center: transformLonLat({
          coordinates: center,
          projection,
          transformTo: true,
        }),
      },
    };

    if (builder) {
      const dataFormBuilder = Object.assign(data, {
        key: "defaultMap",
        _id: id,
      });

      save(dataFormBuilder);
    } else {
      const binaryImg = await createImageFromMap({ map, mapRef: ref });

      data.value = { ...data.value, binaryImg };

      const dataPortal = Object.assign(data, { key: id });
      save(dataPortal);
      lockMap();
    }
  };

  const saveDefaultLayers = (key, layers) => {
    if (builder) {
      const data = {
        _id: id,
        key,
        value: layers,
      };

      save(data);
    }
  };

  const addFeature = (feature) => {
    setLocationFeatures(() => [transformLocationFeature(feature)]);
  };

  const lockMap = () => {
    setStop({});
    setIsSaving(false);
    setIsLocked(true);
  };

  const unlockMap = () => {
    if (disabled) {
      return;
    }

    setIsLocked(false);
    setStop({ next: true });
  };

  const saveMap = () => {
    if (disabled) {
      return;
    }

    _save(map);
    setIsSaving(true);
  };

  const setDisableDnd = (disable) => {
    if (!builder) {
      return;
    }

    setIsDndDisabled(disable);
  };

  return (
    <>
      <MapContainer
        zIndex={1}
        ref={ref}
        onMouseOver={() => setDisableDnd(true)}
        onMouseLeave={() => setDisableDnd(false)}
      >
        {!builder && isLocked && (
          <LockWrapper
            className="lock-wrapper"
            onClick={unlockMap}
            disabled={disabled}
          >
            <LockHeader>
              {intl.formatMessage({ id: "OLMap.Map.clickToEdit" })}
            </LockHeader>
          </LockWrapper>
        )}
        <MapContext.Provider value={{ map }}>
          <Map
            center={transformLonLat({ coordinates: center, projection })}
            zoom={zoom}
            disabled={disabled}
            projection={projection}
            map={map}
            setMap={setMap}
            id={"map"}
          >
            {!disabled && shouldDisplayToolbar && (
              <Interaction
                setLocationFeatures={setLocationFeatures}
                source={drawLayer}
                save={_save}
                parentPosition={
                  ref.current ? ref.current.getBoundingClientRect() : {}
                }
                addressSearchEnabled={addressSearchEnabled}
                disablePoint={disablePoint}
                disableBox={disableBox}
                disableLine={disableLine}
                disablePen={disablePen}
                disableEraser={disableEraser}
                setStop={setStop}
                setIsSaving={setIsSaving}
                saveMap={saveMap}
                drawMode={drawMode}
                setDrawMode={setDrawMode}
              />
            )}
            {drawLayer && (
              <VectorLayer
                style={(feature) => getFeatureStyle(feature)}
                source={drawLayer}
                zIndex={1000}
              />
            )}
            <Layers>
              {!defaultBackgroundDisabled && (
                <TileLayer source={osm()} zIndex={0} />
              )}
              {useArcGIS && selectedBgLayers?.length > 0 && (
                <TileLayer
                  source={tileArcGISRest({
                    url: backgroundUrl,
                    selectedLayers: selectedBgLayers,
                  })}
                  selectedLayers={selectedBgLayers}
                />
              )}
              {useArcGIS && selectedLayers?.length > 0 && (
                <TileLayer
                  source={tileArcGISRest({
                    url: foregroundUrl,
                    selectedLayers,
                  })}
                  selectedLayers={selectedLayers}
                />
              )}
              {useWMTS &&
                wmtsBgXML &&
                selectedBgLayers &&
                selectedBgLayers?.map((layerIndex, index) => {
                  if (index + 1 > selectedBgLayers?.length) return;
                  const layer = backgroundLayers[layerIndex]?.name;
                  return (
                    <>
                      <TileLayer
                        source={wmts({
                          url: backgroundUrl,
                          layers: wmtsBgXML,
                          layer,
                          projection,
                        })}
                        zIndex={index + 1}
                      />
                    </>
                  );
                })}
              {useWMTS &&
                wmtsXML &&
                selectedLayers &&
                selectedLayers?.map((layerIndex, index) => {
                  if (index + 1 > selectedLayers?.length) return;
                  const layer = layers[layerIndex]?.name;
                  return (
                    <>
                      <TileLayer
                        source={wmts({
                          url: foregroundUrl,
                          layers: wmtsXML,
                          layer,
                          projection,
                        })}
                        zIndex={index + 10}
                      />
                    </>
                  );
                })}
              {useWMS &&
                selectedBgLayers &&
                selectedBgLayers?.map((layerIndex, index) => {
                  if (index + 1 > selectedBgLayers?.length) return;
                  const layer = backgroundLayers[layerIndex]?.name;
                  if (!layer) {
                    return null;
                  } else {
                    return (
                      <>
                        <TileLayer
                          source={wms({
                            url: backgroundUrl,
                            layer,
                            projection,
                          })}
                          zIndex={index + 1}
                        />
                      </>
                    );
                  }
                })}
              {useWMS &&
                selectedLayers &&
                selectedLayers?.map((layerIndex, index) => {
                  if (index + 1 > selectedLayers?.length) return;
                  const layer = layers[layerIndex]?.name;
                  if (!layer) {
                    return null;
                  } else {
                    return (
                      <>
                        <TileLayer
                          source={wms({
                            url: foregroundUrl,
                            layer,
                            projection,
                          })}
                          zIndex={index + 10}
                        />
                      </>
                    );
                  }
                })}

              {_locationFeatures?.map((feature, index) => {
                const coordinates = `${feature?.features[0]?.geometry?.coordinates[0]}_${feature?.features[0]?.geometry?.coordinates[1]}`;
                return (
                  <VectorLayer
                    key={`feature_${index}_${coordinates}`}
                    source={vector({
                      features: new GeoJSON().readFeatures(feature, {}),
                    })}
                    style={styles.Point}
                  />
                );
              })}
            </Layers>
            {!builder && shouldDisplayToolComponents && (
              <Controls>
                <FullScreenControl />
              </Controls>
            )}
          </Map>
        </MapContext.Provider>
        {addressSearchEnabled && !disabled && shouldDisplayToolComponents && (
          <SearchField
            {...props}
            searchResult={searchResult}
            addFeature={addFeature}
            hasTriggeredSearch={hasTriggeredSearch}
            map={map}
          ></SearchField>
        )}
        {(builder || shouldDisplayToolComponents) && (
          <SaveButton
            id="ol-map-save-btn"
            onClick={saveMap}
            disabled={drawMode}
          >
            {intl.formatMessage({
              id: builder ? "OLMap.Map.saveCenter" : "OLMap.Map.saveChanges",
            })}
          </SaveButton>
        )}
      </MapContainer>
      {builder &&
        isEmpty(layers) &&
        isEmpty(backgroundLayers) &&
        (useArcGIS || useWMTS || useWMS) &&
        (foregroundUrl || backgroundUrl) && (
          <div>
            <DotLoader />
          </div>
        )}
      {builder && (
        <LayerSelectorContainer>
          {!isEmpty(backgroundLayers) && (
            <LayerSelector
              layers={backgroundLayers}
              setSelectedLayers={setSelectedBgLayers}
              save={(layers) => saveDefaultLayers("defaultBgLayers", layers)}
              defaultLayers={defaultBgLayers}
              label="OLMap.SelectBgLayers"
              style={{ borderBottom: "1px solid black" }}
            />
          )}
          {!isEmpty(layers) && (
            <LayerSelector
              layers={layers}
              setSelectedLayers={setSelectedLayers}
              save={(layers) => saveDefaultLayers("defaultLayers", layers)}
              defaultLayers={defaultLayers}
              label="OLMap.SelectLayers"
            />
          )}
        </LayerSelectorContainer>
      )}
      {!isLocked && (
        <ValidationBar
          error={`OLMap.validation.${drawMode ? "closeDrawMode" : "save"}`}
        />
      )}
      {!builder && isSaving && (
        <SaveInfoWrapper>
          <p>{intl.formatMessage({ id: "OLMap.Map.savingMap" })}</p>
          <DotLoader delay={0} />
        </SaveInfoWrapper>
      )}
    </>
  );
};

export default injectIntl(OpenLayersMAP);
